import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { razdvojVreme, duzinaTeksta } from './segmentRute';
import { googleLinkTacke, otvoriGoogle } from './googleLinkovi';

/* „Podaci o odabranom segmentu rute" — kartica posle klika na rutu.

   Za razliku od kartice stajanja, ova ima DVE kolone vrednosti: „Od" i „Do".
   Deonica je prelaz između dve tačke, pa svaki podatak ima dva kraja i tek se
   iz njihove razlike vidi šta se desilo — usporio, ubrzao, prešao 141 m za
   šest sekundi.

   ── ADRESE STIŽU NAKNADNO ─────────────────────────────────────────────────
   Dva obrnuta geokodiranja, dakle dve sekunde (Nominatim dozvoljava jedan upit
   u sekundi). Traže se tek kad se kartica otvori i stižu nezavisno jedna od
   druge, pa se prva vidi čim je tu, ne kad obe budu gotove. */

const Red = ({ naziv, od, doo, spojeno }) => (
    <div className="gps-seg-red">
        <span className="gps-seg-naziv">{naziv}</span>
        {spojeno
            ? <span className="gps-seg-spojeno">{od}</span>
            : (
                <>
                    <span className="gps-seg-vrednost">{od}</span>
                    <span className="gps-seg-vrednost">{doo}</span>
                </>
            )}
    </div>
);

const PopupSegment = ({ segment, vozilo, zovi }) => {
    const { t, i18n } = useTranslation();
    const [adrese, setAdrese] = useState({ a: null, b: null });
    const [ucitava, setUcitava] = useState(false);

    const { a, b, metara, prosek, datum } = segment;

    useEffect(() => {
        let ziv = true;
        setAdrese({ a: null, b: null });
        setUcitava(true);

        const trazi = (tacka, strana) =>
            zovi('/gps/reverzno', { lat: tacka.lat, lng: tacka.lng, jezik: i18n.language })
                .then(d => { if (ziv) setAdrese(p => ({ ...p, [strana]: d?.adresa || null })); })
                .catch(() => { /* mesto ostaje bez imena, koordinate su i dalje tu */ });

        /* `allSettled`, ne `all`: jedan neuspeh ne sme da ostavi i drugu stranu
           zauvek u stanju „učitavanje". */
        Promise.allSettled([trazi(a, 'a'), trazi(b, 'b')])
            .finally(() => { if (ziv) setUcitava(false); });

        return () => { ziv = false; };
    }, [a, b, zovi, i18n.language]);

    const naziv = [vozilo?.registracija, [vozilo?.marka, vozilo?.model].filter(Boolean).join(' ').trim()]
        .filter(Boolean);

    const adresa = (v) => {
        if (v) return v;
        return <em className="gps-seg-ceka">{ucitava ? t('gps.ucitavanje') : t('gps.stajanjeBezAdrese')}</em>;
    };

    /* Koordinate SU dugme, umesto da dugme stoji pored njih.

       U tri uske kolone dugme pored teksta nije stalo — gurnulo je par preko
       ivice kartice. A i suvisno je: koordinate su jedina stvar u tom redu i
       jedino sto se sa njima radi je otvaranje na mapi, pa nema sta drugo da
       se klikne.

       <button>, a ne <a>: globalni Bootstrap stil za linkove bi mu dao svoju
       boju, podvlacenje i veci font (isto se vec desilo u panelu vozila). */
    const koord = (tacka) => (
        <button
            type="button"
            className="gps-seg-koord"
            title={t('gps.pinOtvoriUGoogle')}
            onClick={() => otvoriGoogle(googleLinkTacke(tacka))}
        >
            {`${Number(tacka.lat).toFixed(4)}, ${Number(tacka.lng).toFixed(4)}`}
        </button>
    );

    return (
        <div className="gps-seg">
            <div className="gps-seg-naslov">{t('gps.segmentNaslov')}</div>

            <Red naziv={t('gps.vozilo')} spojeno
                 od={naziv.length > 1 ? `${naziv[0]} (${naziv[1]})` : naziv[0]} />

            <Red naziv={t('gps.segmentDatum')} spojeno od={datum} />

            <Red naziv={t('gps.segmentProsek')} spojeno
                 od={`${prosek} km/h · ${duzinaTeksta(metara)}`} />

            {/* Zaglavlje Od/Do stoji tek ispred prvog reda koji ima dve strane —
                iznad njega su podaci koji važe za celu deonicu. */}
            <div className="gps-seg-red gps-seg-zaglavlje">
                <span className="gps-seg-naziv" />
                <span className="gps-seg-vrednost">{t('gps.segmentOd')}</span>
                <span className="gps-seg-vrednost">{t('gps.segmentDo')}</span>
            </div>

            <Red naziv={t('gps.segmentVreme')}
                 od={razdvojVreme(a.vreme).vreme}
                 doo={razdvojVreme(b.vreme).vreme} />

            <Red naziv={t('gps.brzina')}
                 od={`${a.brzina ?? 0} km/h`}
                 doo={`${b.brzina ?? 0} km/h`} />

            <Red naziv={t('gps.stajanjeLokacija')}
                 od={adresa(adrese.a)}
                 doo={adresa(adrese.b)} />

            <Red naziv={t('gps.stajanjeKoordinate')}
                 od={koord(a)}
                 doo={koord(b)} />
        </div>
    );
};

export default PopupSegment;
